import { useCallback, useState } from "react";
import { createPopper } from "@popperjs/core";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import ReactDatePicker, {
  DatePickerProps as ReactDatePickerProps,
} from "react-date-picker/dist/entry.nostyle";
import "./DatePicker.css";
import "./Calendar.css";
import { cn } from "utils/cn";

interface DatePickerProps extends ReactDatePickerProps {
  label?: string;
  classNames?: string;
  calendarClassName?: string;
}

// TODO: keep eye on react-date-picker they plan to add portalContainer prop which will simplify calendar positioning
export const DatePicker = ({
  label,
  classNames,
  calendarClassName,
  ...props
}: DatePickerProps) => {
  const { i18n } = useModifiedTranslation();
  const { positionedElementRefCallback, targetRefCallback } =
    usePositionElementToOtherElement();

  return (
    <div className="flex w-full flex-col gap-0" ref={targetRefCallback}>
      {label && (
        <label className="mb-1 text-body-small text-primary-80">{label}</label>
      )}
      <ReactDatePicker
        calendarIcon={
          <span className="mgc_calendar_line text-2xl text-secondary-100"></span>
        }
        clearIcon={null}
        className={cn(
          "h-[46px] rounded border border-primary-20 bg-transparent p-2 text-body-small leading-tight text-primary-100 md:px-3.5 md:py-2",
          classNames
        )}
        calendarClassName={calendarClassName}
        locale={i18n.language}
        inputRef={positionedElementRefCallback}
        {...props}
      />
    </div>
  );
};

const usePositionElementToOtherElement = () => {
  const [targetNode, setTargetNode] = useState<HTMLElement>();

  const targetRefCallback = useCallback((node: HTMLElement | null) => {
    if (!node) {
      return;
    }
    setTargetNode(node);
  }, []);

  const positionedElementRefCallback = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !targetNode) {
        return;
      }
      createPopper(targetNode, node, {
        placement: "bottom-start",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 8], // [x, y] offset - adds 8px margin between input and calendar
            },
          },
          {
            name: "matchWidth",
            enabled: true,
            phase: "beforeWrite",
            requires: ["computeStyles"],
            fn: ({ state }) => {
              const minWidth = 320; // Minimum width in pixels
              const referenceWidth = state.rects.reference.width;
              state.styles.popper.width = `${Math.max(
                referenceWidth,
                minWidth
              )}px`;
            },
            effect: ({ state }) => {
              if (state.elements.reference instanceof HTMLElement) {
                const minWidth = 320; // Minimum width in pixels
                const referenceWidth = state.elements.reference.offsetWidth;
                state.elements.popper.style.width = `${Math.max(
                  referenceWidth,
                  minWidth
                )}px`;
              }
            },
          },
        ],
      });
    },
    [targetNode]
  );

  return {
    positionedElementRefCallback,
    targetRefCallback,
  };
};
